import PropTypes from 'prop-types';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet
} from '@react-pdf/renderer';
import dayjs from 'dayjs';
import hyLogo from 'assets/img/logos/hy-logo.png';

// Brand colours (Help Yateem green + neutral greys)
const GREEN = '#1f8a4c';
const DARK = '#1f2937';
const MUTED = '#6b7280';
const LIGHT = '#f3f4f6';

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10.5, color: DARK, fontFamily: 'Helvetica' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: GREEN,
    paddingBottom: 10,
    marginBottom: 16
  },
  logo: { height: 34 },
  kicker: {
    fontSize: 9,
    color: GREEN,
    letterSpacing: 1.5,
    textTransform: 'uppercase'
  },
  title: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: DARK,
    marginTop: 2
  },
  meta: { fontSize: 9, color: MUTED, marginTop: 4 },
  hero: { flexDirection: 'row', marginBottom: 14 },
  heroPhoto: {
    width: 150,
    height: 150,
    borderRadius: 6,
    objectFit: 'cover',
    marginRight: 14
  },
  heroPhotoPlaceholder: {
    width: 150,
    height: 150,
    borderRadius: 6,
    marginRight: 14,
    backgroundColor: LIGHT,
    alignItems: 'center',
    justifyContent: 'center'
  },
  quoteBox: {
    flex: 1,
    backgroundColor: LIGHT,
    borderLeftWidth: 4,
    borderLeftColor: GREEN,
    padding: 12,
    justifyContent: 'center'
  },
  quote: {
    fontSize: 13,
    fontFamily: 'Helvetica-Oblique',
    color: DARK,
    lineHeight: 1.4
  },
  quoteBy: { fontSize: 9, color: MUTED, marginTop: 6 },
  section: { marginBottom: 10 },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: GREEN,
    marginBottom: 3
  },
  body: { lineHeight: 1.45, color: '#374151' },
  photosRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 6 },
  photo: {
    width: 118,
    height: 90,
    objectFit: 'cover',
    borderRadius: 4,
    marginRight: 6,
    marginBottom: 6
  },
  footer: {
    position: 'absolute',
    bottom: 22,
    left: 32,
    right: 32,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: MUTED
  }
});

/**
 * Shareable case-study card (A4 portrait PDF): the child's photo, the quote,
 * the four sections and supporting photos under Help Yateem branding - for
 * events, sponsor updates and social posts.
 */
const CaseStudyPdf = ({ caseStudy, organisationName = 'Help Yateem' }) => {
  const { orphan, title, quote, study_date, photos } = caseStudy;
  const childName = orphan
    ? [orphan.first_name, orphan.middle_name, orphan.last_name]
        .filter(Boolean)
        .join(' ')
    : '';
  const heroPhoto = orphan?.image || (Array.isArray(photos) ? photos[0] : null);
  const extraPhotos = (Array.isArray(photos) ? photos : []).filter(
    p => p !== heroPhoto
  );
  const sections = [
    ['Life before Help Yateem', caseStudy.life_before],
    ['How they found out about Help Yateem', caseStudy.how_found_out],
    ['Their experience with Help Yateem', caseStudy.experience],
    ['How they are now', caseStudy.life_now]
  ].filter(([, text]) => text);

  return (
    <Document title={`${title} - ${childName}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View>
            <Text style={styles.kicker}>Case Study</Text>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.meta}>
              {childName}
              {orphan?.country ? ` · ${orphan.country}` : ''}
              {study_date ? ` · ${dayjs(study_date).format('MMMM YYYY')}` : ''}
            </Text>
          </View>
          <Image src={hyLogo} style={styles.logo} />
        </View>

        <View style={styles.hero}>
          {heroPhoto ? (
            <Image src={heroPhoto} style={styles.heroPhoto} />
          ) : (
            <View style={styles.heroPhotoPlaceholder}>
              <Text style={{ color: MUTED, fontSize: 9 }}>No photo</Text>
            </View>
          )}
          <View style={styles.quoteBox}>
            {quote ? (
              <>
                <Text style={styles.quote}>“{quote}”</Text>
                <Text style={styles.quoteBy}>
                  — {childName || 'Beneficiary'}
                </Text>
              </>
            ) : (
              <Text style={{ ...styles.body, color: MUTED }}>
                {sections[0]?.[1]?.slice(0, 280) || ''}
              </Text>
            )}
          </View>
        </View>

        {sections.map(([label, text]) => (
          <View key={label} style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>{label}</Text>
            <Text style={styles.body}>{text}</Text>
          </View>
        ))}

        {extraPhotos.length > 0 && (
          <View style={styles.photosRow}>
            {extraPhotos.slice(0, 8).map(url => (
              <Image key={url} src={url} style={styles.photo} />
            ))}
          </View>
        )}

        <View style={styles.footer} fixed>
          <Text>{organisationName} · Changing lives through sponsorship</Text>
          <Text>
            {orphan?.simple_id ? `Beneficiary B${orphan.simple_id} · ` : ''}
            Generated {dayjs().format('DD MMM YYYY')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

CaseStudyPdf.propTypes = {
  caseStudy: PropTypes.object.isRequired,
  organisationName: PropTypes.string
};

export default CaseStudyPdf;
